import { PageSpinner, useNav } from '@armhr/ui';
import { Alert, Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import { paths } from '../../utils/paths';

const isForbidden = (err: Error | null) =>
  (err as any)?.response?.status === 403;

const SwipeclockPtoPage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { setFluid } = useNav();

  const {
    data: ptoUrl,
    loading: ptoLoading,
    error: ptoError,
  } = useApiData<string | null>((api) => api.attendance.getPtoRedirect());

  // Render edge-to-edge so the iframe can own the full content height (no padded
  // container or footer below it).
  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  // The SC_PTO widget is always available when SwipeClock is on.
  if (!user?.is_swipeclock_enabled) {
    navigate(paths.dashboard);
    return null;
  }

  if (ptoLoading) {
    return <PageSpinner />;
  }

  if (isForbidden(ptoError)) {
    navigate(paths.dashboard);
    return null;
  }

  if (ptoError || !ptoUrl) {
    return (
      <Alert severity="error">
        An error occured while loading the PTO page. Please refresh and try
        again
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Helmet>
        <title>Time off | Armhr</title>
        <meta name="description" content="Request and manage your PTO." />
      </Helmet>
      <Box sx={{ display: 'flex', px: 3, py: 2, alignItems: 'center' }}>
        <Typography variant="h2">Time off</Typography>
      </Box>
      <iframe
        src={ptoUrl}
        title="SwipeClock PTO"
        loading="lazy"
        style={{
          background: 'white',
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
    </Box>
  );
};

export default SwipeclockPtoPage;
